import { Injectable } from '@angular/core';
import { User } from 'src/app/shared/models/security/user';
import { LogService } from '../log/log.service';
import {
	AREA_ACTIVE,
	OPTIM_MULTIPLE,
	OPTIM_QUEUE,
	PARAM_FUNDER_ACTIVE,
	PARAM_IS_AVAILABILITY_PLANNING_VISIBLE,
	PARAM_LABEL_HOUR_MAX_DAY,
	PARAM_LABEL_HOUR_MIN_DAY,
	PARAM_OPTIM_PARAMS_EDITABLE,
	PARAM_PREFERENCES_BENEFICIAIRE,
	PARAM_VEHICULE,
	PARAM_VEHICULE_VALUE_AUTO,
	PARAM_VEHICULE_VALUE_OFF
} from '../constant/constants.service';
import { Authority } from 'src/app/shared/models/security/authority';
import { OptimNotification } from 'src/app/shared/models/optim-notification';
import { ServiceCenter } from 'src/app/shared/models/service-center';
import {
	CompetencesElementaires,
	getCompetencesByCenterType,
	hasEmployeeSkill
} from 'src/app/shared/enums/competence.enum';

@Injectable({
	providedIn: 'root'
})
export class DataStorageService {
	JWT_TOKEN_KEY = 'JWT_TOKEN';
	USER = 'USER';
	NOTIFICATION_REFRESH_RATE_KEY = 'NOTIFICATION_REFRESH_RATE';

	constructor(private logger: LogService) {}

	getJwtToken(): string {
		return sessionStorage.getItem(this.JWT_TOKEN_KEY);
	}

	setJwtToken(token: string) {
		try {
			sessionStorage.setItem(this.JWT_TOKEN_KEY, token);
		} catch (e) {
			this.logger.log(
				'Impossible de sauvegarder le token JWT en local storage',
				e
			);
		}
	}

	private hasAuthority(authority: Authority): boolean {
		const user: User | null = this.getUser(); // Assuming getUser() returns a User or null
		return user?.role.id === authority;
	}

	public isPlanificatrice(): boolean {
		return this.hasAuthority(Authority.PLANIFICATEUR);
	}

	public isProjectManager(): boolean {
		return this.hasAuthority(Authority.CHARGE_MISSION);
	}

	public isProfessionnelSAE(): boolean {
		return this.hasAuthority(Authority.PROFESSIONNEL_SAE);
	}

	public isProfessionnelTerrain(): boolean {
		return (
			this.isProfessionnelSAE() &&
			hasEmployeeSkill(this.getUser().employee, CompetencesElementaires.TERRAIN)
		);
	}

	public isIntervenant(): boolean {
		return this.hasAuthority(Authority.INTERVENANT);
	}

	public isPrescriber(): boolean {
		return this.hasAuthority(Authority.PRESCRIPTEUR);
	}

	setUser(user: User): void {
		sessionStorage.setItem(this.USER, user ? JSON.stringify(user) : '');
	}

	getUser(): User {
		return JSON.parse(sessionStorage.getItem(this.USER));
	}

	clearStorage() {
		localStorage.clear();
		sessionStorage.clear();
	}

	setParam(paramName: string, paramValue: any): void {
		sessionStorage.setItem(paramName, JSON.stringify(paramValue));
	}

	getParam(param: string): any {
		return JSON.parse(sessionStorage.getItem(param));
	}

	deleteParam(param: string) {
		sessionStorage.removeItem(param);
	}

	clearSession(): void {
		sessionStorage.clear();
	}

	getParamVehicule(): string {
		return JSON.parse(sessionStorage.getItem(PARAM_VEHICULE));
	}

	getOptimQueue(): OptimNotification[] {
		return JSON.parse(sessionStorage.getItem(OPTIM_QUEUE));
	}

	setOptimQueue(optimNotifs: OptimNotification[]) {
		sessionStorage.setItem(OPTIM_QUEUE, JSON.stringify(optimNotifs));
	}

	isVehiculeActive(): boolean {
		return (
			JSON.parse(sessionStorage.getItem(PARAM_VEHICULE)) !==
			PARAM_VEHICULE_VALUE_OFF
		);
	}

	isOptimVehicleActive(): boolean {
		return (
			JSON.parse(sessionStorage.getItem(PARAM_VEHICULE)) ===
			PARAM_VEHICULE_VALUE_AUTO
		);
	}

	isFunderActive(): boolean {
		return JSON.parse(sessionStorage.getItem(PARAM_FUNDER_ACTIVE)) === 'true';
	}

	isOptimParamsEditable(): boolean {
		return (
			JSON.parse(sessionStorage.getItem(PARAM_OPTIM_PARAMS_EDITABLE)) === 'true'
		);
	}

	isAreaActive(): boolean {
		return JSON.parse(sessionStorage.getItem(AREA_ACTIVE)) === 'true';
	}

	isOptimMultipleActive(): boolean {
		return JSON.parse(sessionStorage.getItem(OPTIM_MULTIPLE)) === 'true';
	}

	getParamMinDay(): number {
		return JSON.parse(sessionStorage.getItem(PARAM_LABEL_HOUR_MIN_DAY));
	}

	getParamMaxDay(): number {
		return JSON.parse(sessionStorage.getItem(PARAM_LABEL_HOUR_MAX_DAY));
	}

	getServiceCenterType(): number {
		const user = this.getUser();
		return user?.employee.serviceCenter.type;
	}

	getServiceCenter(): ServiceCenter {
		const user = this.getUser();
		return user?.employee.serviceCenter;
	}

	isSESSAD(): boolean {
		return this.getServiceCenterType() === 1;
	}

	isSAAD(): boolean {
		return this.getServiceCenterType() === 2;
	}

	isSSIAD(): boolean {
		return this.getServiceCenterType() === 3;
	}

	isSAE(): boolean {
		return this.getServiceCenterType() === 4;
	}

	getCompetencesElementaires() {
		const serviceCenterType = this.getServiceCenterType();
		return getCompetencesByCenterType(serviceCenterType);
	}

	isPreferencesBeneficiaireActive(): boolean {
		return (
			JSON.parse(sessionStorage.getItem(PARAM_PREFERENCES_BENEFICIAIRE)) ===
			'true'
		);
	}

	getParamAvailabilityPlanningActive(): boolean {
		return (
			JSON.parse(
				sessionStorage.getItem(PARAM_IS_AVAILABILITY_PLANNING_VISIBLE)
			) === 'true'
		);
	}

	getLabel(paramLabel: string): string {
		return JSON.parse(sessionStorage.getItem(paramLabel));
	}

	getLabelCapitalized(paramLabel: string): string {
		const label = this.getLabel(paramLabel);
		if (label === null) {
			return null;
		}
		return label.charAt(0).toUpperCase() + label.slice(1);
	}

	getLabelPluralized(paramLabel: string): string {
		return this.getLabel(paramLabel) + 's';
	}

	getLabelCapitalizedPluralized(paramLabel: string): string {
		return this.getLabelCapitalized(paramLabel) + 's';
	}

	removeSessionItem(param: string): void {
		sessionStorage.removeItem(param);
	}

	setRefreshNotificationRateInSeconds(rate: number) {
		sessionStorage.setItem(this.NOTIFICATION_REFRESH_RATE_KEY, '' + rate);
	}

	getRefreshNotificationRateInSeconds(): number {
		return +sessionStorage.getItem(this.NOTIFICATION_REFRESH_RATE_KEY);
	}
}
